import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Skeleton, IconButton, ListItemButton, Paper, Popper, Fade, Tooltip, } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { useTheme, alpha } from '@mui/material/styles';
import { useLocale } from '../../locale/LocaleProvider';
import { translateLandingAgencyDisplayName } from '../../locale/gridTranslations';
import { Link as RouterLink } from 'react-router-dom';
import { getIconByName } from '../../utils/iconMap';
import { isInternalAppPath } from '../../utils/internalPath';
import { resolvePublicUrl } from '../../utils/publicUrl';
import { resolveDepartmentForAgency } from '../../services/landingDepartmentMatch';
import { loadAgencyLogoMap, resolveAgencyLogoFile, resolveAgencyLogoPublicUrl } from '../../services/agencyLogoMap';
import { AgencyDetailTooltipPaper, AgencyGroupAccordionList, agencyDetailTooltipPopperProps, AGENCY_TOOLTIP_FLIP_FROM_TOP, AGENCY_GROUP_PANEL_FLIP_PLACEMENTS, buildDepartmentAgencyHref, } from './LandingPortraitAnchorPanels';
/**
 * When several grid `NAME`s share a long prefix before the first hyphen (e.g. `Bureau of Public Health-OEHS`),
 * they merge into one carousel tile. Min length avoids splitting short labels like `A-B`.
 */
function hyphenPrefixGroup(name) {
    const raw = name.trim();
    if (!raw)
        return null;
    const idx = raw.search(/[-–—]/);
    if (idx < 0)
        return null;
    const prefix = raw.slice(0, idx).trim();
    if (prefix.length < 20)
        return null;
    return { key: `group:__pfx__:${prefix.toLowerCase()}`, label: prefix };
}
/**
 * Same optional `agencyGroup` / bureau → one tile; else shared hyphen-prefix family; else same `NAME` (case-insensitive).
 */
function groupKeyForAgency(a, index) {
    const g = (a.agencyGroup ?? '').trim();
    if (g)
        return `group:${g.toLowerCase()}`;
    const hp = hyphenPrefixGroup(a.name);
    if (hp)
        return hp.key;
    const n = a.name.trim();
    if (n)
        return `name:${n.toLowerCase()}`;
    return `__unnamed__${(a.recordId ?? a.rowId ?? `i${index}`).toString()}`;
}
/** Stable “random” decor icon per agency name until real icons come from the API. */
const LANDING_AGENCY_DECOR_ICONS = [
    'Business',
    'AccountBalance',
    'Gavel',
    'LocalHospital',
    'Forest',
    'DirectionsCar',
    'Public',
    'Apartment',
    'Park',
    'Assignment',
    'VerifiedUser',
    'Groups',
    'Description',
    'Work',
    'PrecisionManufacturing',
    'Diversity3',
];
function decorIconNameForAgency(name) {
    let h = 2166136261;
    for (let i = 0; i < name.length; i++) {
        h ^= name.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    const idx = Math.abs(h) % LANDING_AGENCY_DECOR_ICONS.length;
    return LANDING_AGENCY_DECOR_ICONS[idx];
}
function neutralAgencyTileSurfaces(theme) {
    const card = theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.1)
        : alpha(theme.palette.common.white, 0.16);
    const hover = theme.palette.mode === 'dark'
        ? alpha(theme.palette.grey[900], 0.82)
        : alpha(theme.palette.common.white, 0.32);
    return { card, hover };
}
export function LandingPortraitCarousel({ gridRows, resolvedDepartments, loading, loadError, heading, carouselIntervalMs, shareHeroBackdrop, agenciesBackgroundResolved, agenciesBgPosition, agenciesBgSize, }) {
    const theme = useTheme();
    const ll = theme.landingHeroLayout;
    const { locale, shell } = useLocale();
    const [agencyLogoMap, setAgencyLogoMap] = useState(null);
    const [agencyLogoFailedKeys, setAgencyLogoFailedKeys] = useState([]);
    const agencyCarouselScrollRef = useRef(null);
    const agencyStripMetricsRaf = useRef(null);
    const [agencyStripPageCount, setAgencyStripPageCount] = useState(1);
    const [agencyCarouselPage, setAgencyCarouselPage] = useState(0);
    const [agencyCarouselHover, setAgencyCarouselHover] = useState(false);
    const [agencyTooltipOpen, setAgencyTooltipOpen] = useState(false);
    const [agencyGroupPopover, setAgencyGroupPopover] = useState(null);
    const agencyGroupPopoverOpenRef = useRef(false);
    const agencyGroupPopoverCloseTimer = useRef(null);
    useLayoutEffect(() => {
        agencyGroupPopoverOpenRef.current = agencyGroupPopover != null;
    }, [agencyGroupPopover]);
    const cancelAgencyGroupPopoverClose = useCallback(() => {
        if (agencyGroupPopoverCloseTimer.current !== null) {
            window.clearTimeout(agencyGroupPopoverCloseTimer.current);
            agencyGroupPopoverCloseTimer.current = null;
        }
    }, []);
    const scheduleAgencyGroupPopoverClose = useCallback(() => {
        cancelAgencyGroupPopoverClose();
        agencyGroupPopoverCloseTimer.current = window.setTimeout(() => setAgencyGroupPopover(null), 420);
    }, [cancelAgencyGroupPopoverClose]);
    const openAgencyGroupPopover = useCallback((key, anchor) => {
        cancelAgencyGroupPopoverClose();
        setAgencyGroupPopover({ key, anchor });
    }, [cancelAgencyGroupPopoverClose]);
    useEffect(() => () => {
        cancelAgencyGroupPopoverClose();
    }, [cancelAgencyGroupPopoverClose]);
    useEffect(() => {
        if (!agencyGroupPopover)
            return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                setAgencyGroupPopover(null);
            }
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [agencyGroupPopover]);
    const groupedLandingAgencies = useMemo(() => {
        const map = new Map();
        const order = [];
        gridRows.forEach((item, index) => {
            const key = groupKeyForAgency(item, index);
            if (!map.has(key)) {
                order.push(key);
                map.set(key, []);
            }
            map.get(key).push(item);
        });
        return order.map((k) => ({ key: k, items: map.get(k) }));
    }, [gridRows]);
    const updateAgencyStripMetrics = useCallback(() => {
        const el = agencyCarouselScrollRef.current;
        if (!el)
            return;
        const cw = Math.max(1, el.clientWidth);
        const sw = el.scrollWidth;
        const maxScroll = Math.max(0, sw - cw);
        const pages = Math.max(1, Math.ceil(sw / cw));
        setAgencyStripPageCount(pages);
        setAgencyCarouselPage(maxScroll <= 0 ? 0 : Math.min(pages - 1, Math.round(el.scrollLeft / cw)));
    }, []);
    const scheduleAgencyStripMetrics = useCallback(() => {
        if (agencyGroupPopoverOpenRef.current)
            return;
        if (agencyStripMetricsRaf.current != null)
            return;
        agencyStripMetricsRaf.current = window.requestAnimationFrame(() => {
            agencyStripMetricsRaf.current = null;
            updateAgencyStripMetrics();
        });
    }, [updateAgencyStripMetrics]);
    const scrollAgencyCarouselViewport = useCallback((direction) => {
        const el = agencyCarouselScrollRef.current;
        if (!el)
            return;
        el.scrollBy({ left: direction * el.clientWidth, behavior: 'smooth' });
    }, []);
    const scrollAgencyCarouselToPage = useCallback((pageIndex) => {
        const el = agencyCarouselScrollRef.current;
        if (!el)
            return;
        el.scrollTo({ left: pageIndex * el.clientWidth, behavior: 'smooth' });
    }, []);
    useEffect(() => {
        if (!groupedLandingAgencies.length) {
            setAgencyStripPageCount(1);
            setAgencyCarouselPage(0);
            return;
        }
        const el = agencyCarouselScrollRef.current;
        if (!el)
            return;
        const onScroll = () => scheduleAgencyStripMetrics();
        const onResize = () => updateAgencyStripMetrics();
        el.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onResize);
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
        ro?.observe(el);
        const t = window.setTimeout(onResize, 0);
        const t2 = window.setTimeout(onResize, 150);
        return () => {
            el.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onResize);
            ro?.disconnect();
            clearTimeout(t);
            clearTimeout(t2);
            if (agencyStripMetricsRaf.current != null) {
                window.cancelAnimationFrame(agencyStripMetricsRaf.current);
                agencyStripMetricsRaf.current = null;
            }
        };
    }, [groupedLandingAgencies.length, updateAgencyStripMetrics, scheduleAgencyStripMetrics]);
    useEffect(() => {
        if (!groupedLandingAgencies.length ||
            agencyStripPageCount <= 1 ||
            agencyCarouselHover ||
            agencyTooltipOpen ||
            agencyGroupPopover)
            return;
        const id = window.setInterval(() => {
            const el = agencyCarouselScrollRef.current;
            if (!el)
                return;
            const { scrollLeft, scrollWidth, clientWidth } = el;
            const max = scrollWidth - clientWidth;
            if (max <= 0)
                return;
            if (scrollLeft >= max - 2) {
                el.scrollTo({ left: 0, behavior: 'smooth' });
            }
            else {
                el.scrollBy({ left: clientWidth, behavior: 'smooth' });
            }
        }, carouselIntervalMs);
        return () => clearInterval(id);
    }, [
        groupedLandingAgencies.length,
        agencyStripPageCount,
        agencyCarouselHover,
        agencyTooltipOpen,
        agencyGroupPopover,
        carouselIntervalMs,
    ]);
    useEffect(() => {
        void loadAgencyLogoMap().then(setAgencyLogoMap);
    }, []);
    const agencyCarouselTraySx = useMemo(() => ({
        position: 'relative',
        zIndex: 0,
        ...ll.agencyCarousel.chrome,
        bgcolor: 'transparent',
        border: 'none',
        boxShadow: 'none',
        p: 0,
        boxSizing: 'border-box',
    }), [ll.agencyCarousel.chrome]);
    const pagerInteractive = gridRows.length > 0 && !loading && agencyStripPageCount > 1;
    return ((gridRows.length > 0 || loading || loadError) ? (_jsxs(Box, { id: "agencies", sx: {
            width: '100%',
            alignSelf: 'stretch',
            mt: ll.agencies.marginTop,
            mb: 0,
            position: 'relative',
            zIndex: 4,
            scrollMarginTop: 16,
            overflow: 'visible',
            border: 'none',
            boxShadow: 'none',
        }, children: [!shareHeroBackdrop ? (_jsx(Box, { "aria-hidden": true, sx: {
                    position: 'absolute',
                    inset: 0,
                    zIndex: 0,
                    backgroundImage: `url(${agenciesBackgroundResolved})`,
                    backgroundSize: agenciesBgSize,
                    backgroundPosition: agenciesBgPosition,
                    backgroundRepeat: 'no-repeat',
                } })) : null, _jsxs(Box, { sx: { position: 'relative', zIndex: 2, pt: ll.agencies.panelPaddingTop }, children: [loadError ? (_jsx(Typography, { color: "error", variant: "body2", sx: {
                            py: 1.5,
                            px: { xs: 2, md: 3 },
                            bgcolor: alpha(theme.palette.error.main, 0.12),
                            textAlign: 'center',
                        }, children: shell.heroAgenciesLoadError })) : null, !loadError && ((loading && gridRows.length === 0) || gridRows.length > 0) ? (_jsx(Box, { component: "section", role: gridRows.length > 0 ? 'region' : undefined, "aria-label": heading, "aria-busy": loading && gridRows.length === 0 ? true : undefined, sx: {
                            ...ll.agencyCarousel.section,
                            pt: ll.agencies.sectionPadding.pt,
                            pb: ll.agencies.sectionPadding.pb,
                        }, children: _jsxs(Box, { sx: agencyCarouselTraySx, onMouseEnter: gridRows.length > 0 ? () => setAgencyCarouselHover(true) : undefined, onMouseLeave: gridRows.length > 0 ? () => setAgencyCarouselHover(false) : undefined, children: [loading && gridRows.length === 0 ? (_jsx(Box, { component: "ul", sx: {
                                        display: 'flex',
                                        flexDirection: 'row',
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                        columnGap: { xs: 1.6, sm: 1.8, md: 2, lg: 2.2 },
                                        rowGap: { xs: 1.55, sm: 1.75, md: 1.95, lg: 2.15 },
                                        py: ll.agencyCarousel.list.py,
                                        px: ll.agencyCarousel.list.px,
                                        m: 0,
                                        p: 0,
                                        listStyle: 'none',
                                        overflow: 'visible',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                    }, children: Array.from({ length: 8 }).map((_, i) => (_jsx(Box, { component: "li", sx: {
                                            ...ll.agencyTile.column,
                                            width: {
                                                xs: '100%',
                                                sm: 'calc((100% - 1 * 1.2rem) / 2)',
                                                md: 'calc((100% - 2 * 1.3rem) / 3)',
                                                lg: 'calc((100% - 3 * 1.4rem) / 4)',
                                            },
                                            minWidth: {
                                                xs: '100%',
                                                sm: 'calc((100% - 1 * 1.2rem) / 2)',
                                                md: 'calc((100% - 2 * 1.3rem) / 3)',
                                                lg: 'calc((100% - 3 * 1.4rem) / 4)',
                                            },
                                            maxWidth: {
                                                xs: '100%',
                                                sm: 'calc((100% - 1 * 1.2rem) / 2)',
                                                md: 'calc((100% - 2 * 1.3rem) / 3)',
                                                lg: 'calc((100% - 3 * 1.4rem) / 4)',
                                            },
                                            flexShrink: 0,
                                        }, children: _jsx(Skeleton, { variant: "rounded", animation: "wave", sx: {
                                                width: '100%',
                                                minHeight: ll.agencyTile.portraitMinHeight,
                                                borderRadius: '14px',
                                                bgcolor: alpha(theme.palette.action.hover, 0.12),
                                            } }) }, i))) })) : (_jsx(Box, { sx: {
                                        position: 'relative',
                                        width: '100%',
                                        maxWidth: '100%',
                                        minWidth: 0,
                                    }, children: _jsx(Box, { component: "ul", className: "landing-hero-agency-grid", role: "list", ref: agencyCarouselScrollRef, sx: {
                                            listStyle: 'none',
                                            p: 0,
                                            m: 0,
                                            width: '100%',
                                            minWidth: 0,
                                            display: 'grid',
                                            gridTemplateColumns: {
                                                xs: "repeat(auto-fit, minmax(160px, 1fr))",
                                                sm: "repeat(auto-fit, minmax(180px, 1fr))",
                                                md: "repeat(auto-fit, minmax(200px, 1fr))",
                                                lg: "repeat(auto-fit, minmax(220px, 1fr))",
                                            },
                                            columnGap: { xs: 1.2, sm: 1.6, md: 1.8, lg: 2 },
                                            rowGap: { xs: 1.2, sm: 1.4, md: 1.6, lg: 1.8 },
                                            py: ll.agencyCarousel.list.py,
                                            px: ll.agencyCarousel.list.px,
                                        }, children: groupedLandingAgencies.map(({ key: groupKey, items }, idx) => {
                                            const groupCount = items.length;
                                            const a = items[0];
                                            const href = groupCount === 1 ? buildDepartmentAgencyHref(a) : null;
                                            const displayName = translateLandingAgencyDisplayName(a, locale);
                                            const groupLabelRaw = (a.agencyGroup ?? '').trim();
                                            const bureauGroupTile = groupCount > 1 &&
                                                !!groupLabelRaw &&
                                                items.every((it) => (it.agencyGroup ?? '').trim().toLowerCase() === groupLabelRaw.toLowerCase());
                                            const firstHp = hyphenPrefixGroup(a.name);
                                            const hyphenGroupTile = groupCount > 1 &&
                                                !!firstHp &&
                                                items.every((it) => hyphenPrefixGroup(it.name)?.key === firstHp.key);
                                            const tileTitle = bureauGroupTile
                                                ? groupLabelRaw
                                                : hyphenGroupTile && firstHp
                                                    ? firstHp.label
                                                    : displayName;
                                            const iconName = (a.icon?.trim() ? a.icon : decorIconNameForAgency(a.name));
                                            const Icon = getIconByName(iconName);
                                            const subtitleLine = (a.subtitle ?? '').trim();
                                            const groupedPanelMatchedDept = resolveDepartmentForAgency(a, resolvedDepartments);
                                            const groupedDeptThumbRaw = groupedPanelMatchedDept
                                                ? groupedPanelMatchedDept.logoImage || groupedPanelMatchedDept.image || ''
                                                : '';
                                            const groupedDeptThumbSrc = groupedDeptThumbRaw && !/^data:/i.test(groupedDeptThumbRaw.trim())
                                                ? resolvePublicUrl(groupedDeptThumbRaw.trim()) ?? groupedDeptThumbRaw.trim()
                                                : '';
                                            const groupedDeptHref = groupedPanelMatchedDept?.href?.trim();
                                            const groupedDeptLinkInternal = Boolean(groupedDeptHref && isInternalAppPath(groupedDeptHref));
                                            const logoLookupName = bureauGroupTile && groupLabelRaw
                                                ? groupLabelRaw
                                                : hyphenGroupTile && firstHp
                                                    ? firstHp.label
                                                    : a.name;
                                            const agencyLogoKey = `${a.recordId ?? ''}|${logoLookupName}`;
                                            const agencyLogoFilename = agencyLogoMap &&
                                                resolveAgencyLogoFile(agencyLogoMap, {
                                                    name: logoLookupName,
                                                    recordId: a.recordId,
                                                    agencyGroup: a.agencyGroup,
                                                });
                                            const agencyLogoSrc = agencyLogoFilename && agencyLogoMap
                                                ? resolveAgencyLogoPublicUrl(agencyLogoMap, agencyLogoFilename)
                                                : undefined;
                                            const showMappedAgencyLogo = Boolean(agencyLogoSrc) && !agencyLogoFailedKeys.includes(agencyLogoKey);
                                            const agencySurface = neutralAgencyTileSurfaces(theme);
                                            const agencyPortraitSx = {
                                                ...ll.agencyTile.portrait,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start',
                                                minHeight: { xs: '7rem', md: '8.5rem' },
                                                py: { xs: 1.25, md: 1.5 },
                                                bgcolor: agencySurface.card,
                                                background: theme.palette.mode === 'dark'
                                                    ? agencySurface.card
                                                    : `linear-gradient(140deg, ${alpha(theme.palette.common.white, 0.34)} 0%, ${alpha(theme.palette.common.white, 0.14)} 100%)`,
                                                border: `1px solid ${alpha(theme.palette.common.white, 0.46)}`,
                                                borderRadius: '18px',
                                                boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`,
                                                WebkitBackdropFilter: 'blur(10px)',
                                                backdropFilter: 'blur(10px)',
                                                p: { xs: 1, md: 1.25 },
                                                gap: 1,
                                                '&.MuiListItemButton-root': {
                                                    minHeight: { xs: '7rem', md: '8.5rem' },
                                                    alignItems: 'center',
                                                },
                                                '&:hover': {
                                                    bgcolor: agencySurface.hover,
                                                    background: theme.palette.mode === 'dark'
                                                        ? agencySurface.hover
                                                        : `linear-gradient(140deg, ${alpha(theme.palette.common.white, 0.48)} 0%, ${alpha(theme.palette.common.white, 0.26)} 100%)`,
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: `0 14px 32px ${alpha(theme.palette.common.black, 0.12)}`,
                                                    borderColor: alpha(theme.palette.common.white, 0.46),
                                                },
                                                '&.MuiListItemButton-root:hover': {
                                                    backgroundColor: agencySurface.hover,
                                                },
                                                '&:focus-visible': {
                                                    outline: `2px solid ${theme.palette.primary.main}`,
                                                    outlineOffset: 2,
                                                },
                                                '@media (prefers-reduced-motion: reduce)': {
                                                    transition: 'background-color 0.15s ease',
                                                    '& .landing-agency-logo': {
                                                        WebkitBackdropFilter: 'none',
                                                        backdropFilter: 'none',
                                                        bgcolor: alpha(theme.palette.common.white, 0.94),
                                                        border: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
                                                        boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.75)}`,
                                                    },
                                                    '& .landing-agency-name-badge': {
                                                        WebkitBackdropFilter: 'none',
                                                        backdropFilter: 'none',
                                                        borderRadius: '12px',
                                                        bgcolor: alpha(theme.palette.grey[50], 0.98),
                                                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                                    },
                                                },
                                            };
                                            /** Fluid width from screen size (large max) so copy wraps wide → less vertical growth. */
                                            const tooltipMaxW = ll.agencyTooltip.maxWidth;
                                            const singleAgencyTooltipPaper = groupCount === 1 ? (_jsx(AgencyDetailTooltipPaper, { agency: a, locale: locale, shell: shell, theme: theme, resolvedDepartments: resolvedDepartments, tooltipMaxW: tooltipMaxW })) : null;
                                            const logoBadge = (_jsx(Box, { sx: {
                                                    width: showMappedAgencyLogo ? { xs: 60, md: 72 } : { xs: 44, md: 52 },
                                                    height: { xs: 44, md: 52 },
                                                    minWidth: showMappedAgencyLogo ? { xs: 60, md: 72 } : { xs: 44, md: 52 },
                                                    borderRadius: showMappedAgencyLogo ? '11px' : '50%',
                                                    bgcolor: alpha(theme.palette.common.white, showMappedAgencyLogo ? 0.95 : 0.88),
                                                    border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    overflow: 'hidden',
                                                    boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.82)}`,
                                                    p: showMappedAgencyLogo ? { xs: 0.34, md: 0.4 } : { xs: 0.36, md: 0.42 },
                                                    flexShrink: 0,
                                                }, children: _jsx(Box, { sx: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: showMappedAgencyLogo && agencyLogoSrc ? (_jsx(Box, { component: "img", src: agencyLogoSrc, alt: "", onError: () => setAgencyLogoFailedKeys((prev) => prev.includes(agencyLogoKey) ? prev : [...prev, agencyLogoKey]), sx: {
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'contain',
                                                            objectPosition: 'center',
                                                            display: 'block',
                                                            imageRendering: 'auto',
                                                            filter: 'contrast(1.04) saturate(1.06)',
                                                        } })) : (_jsx(Icon, { sx: {
                                                            fontSize: { xs: '1.2rem', md: '1.35rem' },
                                                            color: alpha(theme.palette.primary.main, 0.42),
                                                        } })) }) }));
                                            const logoBadgeWithTooltip = groupCount === 1 && singleAgencyTooltipPaper ? (_jsx(Tooltip, { arrow: true, describeChild: true, disableInteractive: true, placement: "top", enterDelay: 200, enterNextDelay: 150, onOpen: () => setAgencyTooltipOpen(true), onClose: () => setAgencyTooltipOpen(false), PopperProps: agencyDetailTooltipPopperProps(theme, {
                                                    flipFallbackPlacements: AGENCY_TOOLTIP_FLIP_FROM_TOP,
                                                    popperOffset: [0, 14],
                                                }), componentsProps: {
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: 'transparent',
                                                            p: 0,
                                                            boxSizing: 'border-box',
                                                            maxWidth: `${tooltipMaxW} !important`,
                                                            width: 'max-content',
                                                            minWidth: 0,
                                                            overflow: 'visible',
                                                            pointerEvents: 'none',
                                                        },
                                                    },
                                                    arrow: {
                                                        sx: {
                                                            color: alpha(theme.palette.common.white, 0.97),
                                                            pointerEvents: 'none',
                                                        },
                                                    },
                                                }, title: singleAgencyTooltipPaper, children: _jsx(Box, { component: "span", sx: { display: 'inline-flex', flexShrink: 0 }, children: logoBadge }) })) : groupCount > 1 ? (_jsx(Box, { component: "span", sx: { display: 'inline-flex', flexShrink: 0 }, onMouseEnter: (e) => {
                                                    cancelAgencyGroupPopoverClose();
                                                    openAgencyGroupPopover(groupKey, e.currentTarget);
                                                }, onMouseLeave: scheduleAgencyGroupPopoverClose, children: logoBadge })) : (logoBadge);
                                            const inner = (_jsxs(Box, { sx: {
                                                    width: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }, children: [_jsx(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: logoBadgeWithTooltip }), _jsx(Typography, { variant: "body2", component: "div", sx: {
                                                            fontWeight: 700,
                                                            lineHeight: 1.18,
                                                            fontSize: { xs: '0.95rem', md: '1rem' },
                                                            color: alpha(theme.palette.primary.dark, 0.96),
                                                            textAlign: 'center',
                                                            display: '-webkit-box',
                                                            WebkitBoxOrient: 'vertical',
                                                            WebkitLineClamp: 2,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }, children: tileTitle })] }));
                                            const agencyButton = href ? (_jsx(ListItemButton, { component: RouterLink, to: href, sx: agencyPortraitSx, children: inner })) : groupCount > 1 ? (_jsx(ListItemButton, { component: "button", type: "button", sx: agencyPortraitSx, "aria-label": `${tileTitle}. ${shell.heroAgencyGroupedBadgeAria.replace('{count}', String(groupCount))}`, children: inner })) : (_jsx(ListItemButton, { component: "button", disabled: true, sx: { ...agencyPortraitSx, opacity: 0.72 }, children: inner }));
                                            return (_jsx(Box, { component: "li", className: "landing-agency-card-enter", sx: {
                                                    ...ll.agencyTile.column,
                                                    animationDelay: `${idx * 42}ms`,
                                                    display: 'block',
                                                }, children: groupCount > 1 ? (_jsxs(_Fragment, { children: [_jsx(Box, { sx: { width: '100%', height: '100%', display: 'block' }, children: agencyButton }), _jsx(Popper, { open: agencyGroupPopover?.key === groupKey, anchorEl: agencyGroupPopover?.key === groupKey ? agencyGroupPopover.anchor : null, placement: "top-start", transition: true, sx: { zIndex: (z) => z.zIndex.tooltip }, popperOptions: { strategy: 'fixed' }, modifiers: [
                                                                {
                                                                    name: 'offset',
                                                                    enabled: true,
                                                                    options: { offset: [0, 12] },
                                                                },
                                                                {
                                                                    name: 'preventOverflow',
                                                                    enabled: true,
                                                                    options: {
                                                                        boundary: 'viewport',
                                                                        rootBoundary: 'viewport',
                                                                        altAxis: true,
                                                                        padding: 12,
                                                                    },
                                                                },
                                                                {
                                                                    name: 'flip',
                                                                    enabled: true,
                                                                    options: {
                                                                        boundary: 'viewport',
                                                                        rootBoundary: 'viewport',
                                                                        fallbackPlacements: [...AGENCY_GROUP_PANEL_FLIP_PLACEMENTS],
                                                                    },
                                                                },
                                                            ], children: ({ TransitionProps }) => (_jsx(Fade, { ...TransitionProps, timeout: 120, children: _jsxs(Paper, { elevation: 0, onMouseEnter: cancelAgencyGroupPopoverClose, onMouseLeave: scheduleAgencyGroupPopoverClose, role: "dialog", "aria-label": tileTitle, sx: {
                                                                        boxSizing: 'border-box',
                                                                        width: 'max-content',
                                                                        minWidth: 0,
                                                                        maxWidth: {
                                                                            xs: 'calc(100vw - 16px)',
                                                                            sm: 'min(520px, calc(100vw - 24px))',
                                                                        },
                                                                        maxHeight: 'min(calc(100dvh - 96px), 440px)',
                                                                        overflow: 'auto',
                                                                        borderRadius: 2,
                                                                        bgcolor: alpha(theme.palette.common.white, 0.98),
                                                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                                                        boxShadow: theme.shadows[8] ??
                                                                            `0 10px 28px ${alpha(theme.palette.common.black, 0.14)}`,
                                                                        pointerEvents: 'auto',
                                                                    }, children: [_jsxs(Box, { sx: {
                                                                                px: 1.75,
                                                                                pt: 1.5,
                                                                                pb: 1.25,
                                                                                borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                                                            }, children: [_jsxs(Box, { sx: {
                                                                                        display: 'flex',
                                                                                        alignItems: 'flex-start',
                                                                                        gap: 1.5,
                                                                                        mb: shell.heroAgencyGroupedTooltipLead.trim() ? 0.75 : 0,
                                                                                    }, children: [_jsx(Box, { sx: {
                                                                                                flexShrink: 0,
                                                                                                width: 52,
                                                                                                height: 52,
                                                                                                borderRadius: 1.5,
                                                                                                overflow: 'hidden',
                                                                                                bgcolor: alpha(theme.palette.grey[100], 0.95),
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                                justifyContent: 'center',
                                                                                                border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                                                                                            }, children: groupedDeptThumbSrc ? (_jsx(Box, { component: "img", src: groupedDeptThumbSrc, alt: "", sx: {
                                                                                                    width: '100%',
                                                                                                    height: '100%',
                                                                                                    objectFit: 'contain',
                                                                                                    display: 'block',
                                                                                                } })) : (_jsx(ArticleOutlinedIcon, { sx: {
                                                                                                    fontSize: 30,
                                                                                                    color: theme.palette.primary.main,
                                                                                                    opacity: 0.85,
                                                                                                } })) }), _jsxs(Box, { sx: { minWidth: 0, flex: 1 }, children: [_jsx(Typography, { variant: "caption", component: "div", sx: {
                                                                                                        display: 'block',
                                                                                                        letterSpacing: '0.12em',
                                                                                                        textTransform: 'uppercase',
                                                                                                        fontWeight: 700,
                                                                                                        fontSize: '0.7rem',
                                                                                                        color: alpha(theme.palette.primary.main, 0.84),
                                                                                                        mb: 0.35,
                                                                                                        lineHeight: 1.2,
                                                                                                    }, children: shell.heroAgencyTooltipEyebrow }), _jsx(Box, { sx: {
                                                                                                        display: 'flex',
                                                                                                        flexWrap: 'wrap',
                                                                                                        alignItems: 'baseline',
                                                                                                        gap: 0.5,
                                                                                                        columnGap: 0.75,
                                                                                                        rowGap: 0.35,
                                                                                                    }, children: _jsx(Typography, { variant: "subtitle1", component: "span", sx: {
                                                                                                            fontWeight: 700,
                                                                                                            color: 'text.primary',
                                                                                                            letterSpacing: '-0.02em',
                                                                                                            lineHeight: 1.3,
                                                                                                            fontSize: '1.08rem',
                                                                                                        }, children: tileTitle }) })] })] }), shell.heroAgencyGroupedTooltipLead.trim() ? (_jsx(Typography, { variant: "caption", sx: {
                                                                                        display: 'block',
                                                                                        mt: 0.25,
                                                                                        color: alpha(theme.palette.text.primary, 0.84),
                                                                                        lineHeight: 1.5,
                                                                                        fontSize: '0.86rem',
                                                                                    }, children: shell.heroAgencyGroupedTooltipLead })) : null] }), _jsx(Box, { sx: { px: 0.5, py: 0.5 }, children: _jsx(AgencyGroupAccordionList, { items: items, locale: locale, shell: {
                                                                                    heroAgencyHoverFallback: shell.heroAgencyHoverFallback,
                                                                                    heroAgencyGroupedOpenPermits: shell.heroAgencyGroupedOpenPermits,
                                                                                }, theme: theme, onAfterNavigate: () => setAgencyGroupPopover(null) }) })] }) })) })] })) : (agencyButton) }, `${groupKey}-${idx}`));
                                        }) }) })), _jsx(Box, { sx: {
                                        display: 'none',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: ll.agencyCarousel.pager.gap,
                                        px: { xs: 0.5, sm: 0 },
                                        maxWidth: '100%',
                                        flexWrap: 'wrap',
                                        mt: ll.agencyCarousel.pager.marginTop,
                                        minHeight: ll.agencyCarousel.pager.rowReservedMinPx,
                                        opacity: pagerInteractive ? 1 : 0,
                                        pointerEvents: pagerInteractive ? 'auto' : 'none',
                                        transition: 'opacity 0.38s ease',
                                        '@media (prefers-reduced-motion: reduce)': {
                                            transition: 'none',
                                        },
                                    }, "aria-hidden": !pagerInteractive, children: agencyStripPageCount > 1 ? (_jsxs(Box, { sx: {
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: ll.agencyCarousel.pager.gap,
                                            maxWidth: '100%',
                                            flexWrap: 'wrap',
                                        }, children: [_jsx(IconButton, { type: "button", size: "small", "aria-label": shell.heroAgencyCarouselPrev, onClick: () => scrollAgencyCarouselViewport(-1), sx: {
                                                    width: ll.agencyCarousel.pager.iconButtonPx,
                                                    height: ll.agencyCarousel.pager.iconButtonPx,
                                                    minWidth: ll.agencyCarousel.pager.iconButtonPx,
                                                    p: 0,
                                                    flexShrink: 0,
                                                    bgcolor: '#fff',
                                                    color: theme.palette.primary.main,
                                                    border: `1px solid ${alpha('#ccc', 0.7)}`,
                                                    boxShadow: `0 2px 10px ${alpha('#000', 0.1)}`,
                                                    transform: 'translateY(-6px)',
                                                    transition: 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.25s ease, box-shadow 0.25s ease',
                                                    '& .MuiSvgIcon-root': { color: theme.palette.primary.main },
                                                    '&:hover': {
                                                        bgcolor: '#f5f5f5',
                                                        color: theme.palette.primary.main,
                                                        transform: 'translateY(-6px) scale(1.08)',
                                                        boxShadow: `0 4px 16px ${alpha('#000', 0.15)}`,
                                                        '& .MuiSvgIcon-root': { color: theme.palette.primary.main },
                                                    },
                                                    '&:active': {
                                                        transform: 'translateY(-6px) scale(0.94)',
                                                    },
                                                    '@media (prefers-reduced-motion: reduce)': {
                                                        transition: 'background-color 0.2s ease',
                                                        '&:hover': {
                                                            transform: 'translateY(-6px)',
                                                        },
                                                        '&:active': {
                                                            transform: 'translateY(-6px)',
                                                        },
                                                    },
                                                }, children: _jsx(ChevronLeftIcon, { sx: {
                                                        fontSize: ll.agencyCarousel.pager.chevronIconSize,
                                                        color: theme.palette.primary.main,
                                                    } }) }), agencyStripPageCount <= 18 ? (_jsx(Box, { component: "nav", "aria-label": heading, sx: {
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: ll.agencyCarousel.pager.dotNavGap,
                                                    rowGap: 0.55,
                                                    maxWidth: '100%',
                                                    minHeight: ll.agencyCarousel.pager.dotMinHeightPx,
                                                }, children: Array.from({ length: agencyStripPageCount }, (_, i) => (_jsx(Box, { component: "button", type: "button", onClick: () => scrollAgencyCarouselToPage(i), "aria-label": shell.heroAgencyCarouselDotAria
                                                        .replace('{page}', String(i + 1))
                                                        .replace('{total}', String(agencyStripPageCount)), "aria-current": i === agencyCarouselPage ? 'true' : undefined, sx: {
                                                        appearance: 'none',
                                                        border: 'none',
                                                        m: 0,
                                                        p: 0,
                                                        cursor: 'pointer',
                                                        width: ll.agencyCarousel.pager.dotHitPx,
                                                        height: ll.agencyCarousel.pager.dotHitPx,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        bgcolor: 'transparent',
                                                        flexShrink: 0,
                                                        transition: 'transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
                                                        '&:hover': {
                                                            transform: 'scale(1.12)',
                                                        },
                                                        '&:hover .agency-carousel-dot': {
                                                            bgcolor: i === agencyCarouselPage
                                                                ? theme.palette.primary.dark
                                                                : alpha(theme.palette.secondary.main, 0.75),
                                                        },
                                                        '&:focus-visible': {
                                                            outline: 'none',
                                                        },
                                                        '&:focus-visible .agency-carousel-dot': {
                                                            boxShadow: `0 0 0 2px ${theme.palette.common.white}, 0 0 0 4px ${theme.palette.primary.main}`,
                                                        },
                                                        '@media (prefers-reduced-motion: reduce)': {
                                                            transition: 'none',
                                                            '&:hover': { transform: 'none' },
                                                        },
                                                    }, children: _jsx(Box, { className: "agency-carousel-dot", "aria-hidden": true, sx: {
                                                            width: i === agencyCarouselPage
                                                                ? ll.agencyCarousel.pager.dotActiveWidthPx
                                                                : ll.agencyCarousel.pager.dotInactiveWidthPx,
                                                            height: ll.agencyCarousel.pager.dotHeightPx,
                                                            borderRadius: 999,
                                                            bgcolor: i === agencyCarouselPage
                                                                ? theme.palette.primary.main
                                                                : alpha(theme.palette.primary.main, 0.22),
                                                            boxShadow: i === agencyCarouselPage
                                                                ? `0 0 0 2px ${alpha(theme.palette.secondary.main, 0.35)}`
                                                                : 'none',
                                                            transition: 'background-color 0.3s ease, width 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease',
                                                        } }) }, i))) })) : null, _jsx(IconButton, { type: "button", size: "small", "aria-label": shell.heroAgencyCarouselNext, onClick: () => scrollAgencyCarouselViewport(1), sx: {
                                                    width: ll.agencyCarousel.pager.iconButtonPx,
                                                    height: ll.agencyCarousel.pager.iconButtonPx,
                                                    minWidth: ll.agencyCarousel.pager.iconButtonPx,
                                                    p: 0,
                                                    flexShrink: 0,
                                                    bgcolor: '#fff',
                                                    color: theme.palette.primary.main,
                                                    border: `1px solid ${alpha('#ccc', 0.7)}`,
                                                    boxShadow: `0 2px 10px ${alpha('#000', 0.1)}`,
                                                    transform: 'translateY(-6px)',
                                                    transition: 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.25s ease, box-shadow 0.25s ease',
                                                    '& .MuiSvgIcon-root': { color: theme.palette.primary.main },
                                                    '&:hover': {
                                                        bgcolor: '#f5f5f5',
                                                        color: theme.palette.primary.main,
                                                        transform: 'translateY(-6px) scale(1.08)',
                                                        boxShadow: `0 4px 16px ${alpha('#000', 0.15)}`,
                                                        '& .MuiSvgIcon-root': { color: theme.palette.primary.main },
                                                    },
                                                    '&:active': {
                                                        transform: 'translateY(-6px) scale(0.94)',
                                                    },
                                                    '@media (prefers-reduced-motion: reduce)': {
                                                        transition: 'background-color 0.2s ease',
                                                        '&:hover': {
                                                            transform: 'translateY(-6px)',
                                                        },
                                                        '&:active': {
                                                            transform: 'translateY(-6px)',
                                                        },
                                                    },
                                                }, children: _jsx(ChevronRightIcon, { sx: {
                                                        fontSize: ll.agencyCarousel.pager.chevronIconSize,
                                                        color: theme.palette.primary.main,
                                                    } }) })] })) : null })] }) })) : null] })] })) : null);
}
